/**
 * Reports A La Carte modal.
 *
 * Triggered by a button on the Pullman dashboard. Three selection paths:
 *
 *   1. Whole service / Upcoming — quick view of the next 7 services with
 *      TODAY/TOMORROW callouts. Default view; what stewards want most days.
 *
 *   2. Whole service / Find by date — date-range picker for further-out or
 *      historical services that aren't in the upcoming pane.
 *
 *   3. Just one card — search-by-name-or-booking-ref to print a single
 *      menu card (kitchen reprint, mid-service amendments).
 *
 * In R3 the Generate PDF button stubs to a console.log + alert so the UI
 * flow can be exercised end-to-end without rendering work. R4 brings the
 * five art-deco SVG templates; R5 wires jsPDF + svg2pdf for the actual
 * download.
 *
 * Closes on Esc or backdrop click.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CARD_SIZE_REGISTRY,
  DEFAULT_PRINT_OPTIONS,
  TEMPLATE_REGISTRY,
} from "./types";
import type {
  CardSize,
  MenuCardBooking,
  MenuCardPrintOptions,
  MenuCardService,
  PrintMode,
  TemplateStyle,
} from "./types";
import {
  useMenuCardServices,
  useMenuCardsForService,
  useMenuCardBookingSearch,
  useTriggerMenuCardsRefresh,
} from "./useMenuCards";
import { ReportThumbnail } from "./ReportThumbnail";
import { DietaryCommentsDialog } from "./DietaryCommentsDialog";
import { CarriageLabelsPane } from "./CarriageLabelsPane";
import {
  buildMenuCardsPdf,
  downloadPdf,
  previewPdf,
  filenameForWholeService,
  filenameForSingleCard,
} from "./pdfBuilder";

// ---------------------------------------------------------------------------
// Date helpers — mirror App.tsx formatting so the modal feels native.
// ---------------------------------------------------------------------------
function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatPrettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function extractDeparture(time_label: string): string {
  const space = time_label.indexOf(" ");
  return space === -1 ? time_label : time_label.slice(space + 1);
}

/**
 * "TODAY" / "TOMORROW" / "Sun 11 May" — used in upcoming-services so the
 * next-up service is visually obvious.
 */
function daySuperLabel(iso: string): { tag: string | null; date: string } {
  const today = todayIso();
  const tomorrow = addDaysIso(today, 1);
  const d = new Date(iso + "T00:00:00Z");
  const pretty = d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
  if (iso === today) return { tag: "TODAY", date: pretty };
  if (iso === tomorrow) return { tag: "TOMORROW", date: pretty };
  return { tag: null, date: pretty };
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export interface ReportsALaCarteModalProps {
  open: boolean;
  onClose: () => void;
}

type ReportKey = "pullman-menu-cards" | "travel-and-treats" | "carriage-labels";
type SelectionMode = "service" | "booking";
type ServiceTab = "upcoming" | "date-range";

interface ReportCatalogEntry {
  key: ReportKey;
  name: string;
  blurb: string;
  available: boolean;
  badge?: string;
}

const REPORT_CATALOG: ReportCatalogEntry[] = [
  {
    key: "pullman-menu-cards",
    name: "Pullman Menu Cards",
    blurb:
      "One card per booking (A6 or A5), showing the table, the guest name, and their pre-ordered menu. Print on heavy stock and place on the table before service.",
    available: true,
  },
  {
    key: "travel-and-treats",
    name: "Travel & Treats",
    blurb:
      "One card per booking for Fish & Chips and Travel & Treats services — guest, party size and any pre-orders. Print on heavy stock for the table.",
    available: true,
  },
  {
    key: "carriage-labels",
    name: "Carriage Labels",
    blurb:
      "A5 window labels for group bookings — one per seat-cluster, showing the group, the route and departure time. Pick a service or a date range, then print.",
    available: true,
  },
];

const UPCOMING_LIMIT = 7;
const UPCOMING_RANGE_DAYS = 30;

export function ReportsALaCarteModal({ open, onClose }: ReportsALaCarteModalProps) {
  const [selectedReport, setSelectedReport] = useState<ReportKey>("pullman-menu-cards");
  const [mode, setMode] = useState<SelectionMode>("service");

  const [serviceTab, setServiceTab] = useState<ServiceTab>("upcoming");
  const [rangeFrom, setRangeFrom] = useState<string>(() => todayIso());
  const [rangeTo, setRangeTo] = useState<string>(() => addDaysIso(todayIso(), 30));
  const [selectedService, setSelectedService] = useState<MenuCardService | null>(null);

  const [searchQ, setSearchQ] = useState<string>("");
  const [selectedBooking, setSelectedBooking] = useState<MenuCardBooking | null>(null);

  const [printOptions, setPrintOptions] = useState<MenuCardPrintOptions>(DEFAULT_PRINT_OPTIONS);
  // Edit Dietary Comments — the per-service editor over the selected service's cards.
  const [dietaryOpen, setDietaryOpen] = useState(false);

  // Fresh state every open.
  useEffect(() => {
    if (open) {
      setSelectedService(null);
      setSelectedBooking(null);
      setSearchQ("");
      setMode("service");
      setServiceTab("upcoming");
      setDietaryOpen(false);
    }
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Both card reports (Pullman + Travel & Treats) drive the same service/booking
  // pane; they differ only by journey-type `group` and the card template used.
  const isCardReport =
    selectedReport === "pullman-menu-cards" || selectedReport === "travel-and-treats";
  const isTravelTreat = selectedReport === "travel-and-treats";
  const isCarriageLabels = selectedReport === "carriage-labels";
  const cardGroup = isTravelTreat ? "travel-treats" : "pullman";
  const enableServiceFetch = open && isCardReport && mode === "service";
  const enableBookingFetch = open && isCardReport && mode === "booking";

  // Upcoming services: today .. today+30, take the first N on the client.
  const upcomingFrom = useMemo(() => todayIso(), []);
  const upcomingTo = useMemo(() => addDaysIso(upcomingFrom, UPCOMING_RANGE_DAYS), [upcomingFrom]);
  const upcomingServices = useMenuCardServices(
    upcomingFrom,
    upcomingTo,
    enableServiceFetch && serviceTab === "upcoming",
    cardGroup
  );

  const rangeServices = useMenuCardServices(
    rangeFrom,
    rangeTo,
    enableServiceFetch && serviceTab === "date-range",
    cardGroup
  );

  const cards = useMenuCardsForService(
    mode === "service" ? selectedService?.service_date ?? null : null,
    mode === "service" ? selectedService?.service_time_label ?? null : null,
    mode === "service" ? selectedService?.journey_description ?? null : null
  );

  const search = useMenuCardBookingSearch(
    searchQ,
    null,
    null,
    enableBookingFetch,
    cardGroup
  );

  const activeServices = serviceTab === "upcoming" ? upcomingServices : rangeServices;

  const refresh = useTriggerMenuCardsRefresh(
    useCallback(() => {
      void upcomingServices.refresh();
      void rangeServices.refresh();
      void search.refresh();
    }, [upcomingServices, rangeServices, search])
  );

  // ----- Generation state ---------------------------------------------------
  // `generating` is true while a PDF is being built (whether for download
  // or preview). `progress` ticks per booking in service mode (null for
  // single-card preview, where a single page renders too fast to bother).
  // `genError` carries any builder failure into the footer for the user.
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  // Reset generation messaging whenever the user changes their mind about
  // what to print — stale errors hanging around on a different selection
  // are confusing.
  useEffect(() => {
    setGenError(null);
    setProgress(null);
  }, [mode, selectedService, selectedBooking, printOptions.template, printOptions.printMode, printOptions.size]);

  // ----- Generate (download) ------------------------------------------------
  const onGenerate = useCallback(async () => {
    let bookings: MenuCardBooking[] = [];
    let filename: string;

    if (mode === "service") {
      if (!selectedService || !cards.data) return;
      bookings = cards.data.bookings;
      filename = filenameForWholeService(cards.data.service, printOptions.size);
    } else {
      if (!selectedBooking) return;
      bookings = [selectedBooking];
      filename = filenameForSingleCard(selectedBooking, printOptions.size);
    }

    if (bookings.length === 0) {
      setGenError("No bookings to print on this selection.");
      return;
    }

    setGenerating(true);
    setGenError(null);
    setProgress({ current: 0, total: bookings.length });

    try {
      const result = await buildMenuCardsPdf({
        bookings,
        options: {
          ...printOptions,
          template: isTravelTreat ? "travel-treat" : printOptions.template,
        },
        onProgress: (current, total) => setProgress({ current, total }),
      });

      downloadPdf(result.blob, filename);

      if (result.failed.length > 0) {
        setGenError(
          `${result.failed.length} card${result.failed.length === 1 ? "" : "s"} failed to render and ${result.failed.length === 1 ? "was" : "were"} skipped — see browser console for details.`
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Reports A La Carte] Generate failed:", err);
      setGenError(`Couldn't generate PDF: ${msg}`);
    } finally {
      setGenerating(false);
      // Don't clear progress yet — leaves the "X of Y" visible briefly so
      // the user sees it completed. Clears on the next selection change.
    }
  }, [mode, selectedService, selectedBooking, cards.data, printOptions, isTravelTreat]);

  // ----- Preview (open first card in a new tab, no download) ---------------
  const onPreview = useCallback(async () => {
    let firstBooking: MenuCardBooking | null = null;

    if (mode === "service") {
      firstBooking = cards.data?.bookings[0] ?? null;
    } else {
      firstBooking = selectedBooking;
    }

    if (!firstBooking) return;

    setGenerating(true);
    setGenError(null);

    try {
      const result = await buildMenuCardsPdf({
        bookings: [firstBooking],
        options: {
          ...printOptions,
          template: isTravelTreat ? "travel-treat" : printOptions.template,
        },
      });
      previewPdf(result.blob);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Reports A La Carte] Preview failed:", err);
      setGenError(`Couldn't render preview: ${msg}`);
    } finally {
      setGenerating(false);
    }
  }, [mode, selectedBooking, cards.data, printOptions, isTravelTreat]);

  if (!open) return null;

  const hasBookingsToGenerate =
    mode === "service"
      ? Boolean(selectedService && cards.data && !cards.loading && cards.data.bookings.length > 0)
      : Boolean(selectedBooking);

  const generateDisabled = generating || !hasBookingsToGenerate;
  const previewDisabled = generating || !hasBookingsToGenerate;

  const generateLabel = (() => {
    if (generating && progress && mode === "service" && progress.total > 1) {
      return `Generating… ${progress.current} / ${progress.total}`;
    }
    if (generating) {
      return "Generating…";
    }
    if (mode === "service" && cards.data) {
      const n = cards.data.bookings.length;
      return `Generate PDF (${n} card${n === 1 ? "" : "s"})`;
    }
    if (mode === "booking") {
      return "Generate single card PDF";
    }
    return "Generate PDF";
  })();

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal modal--alacarte"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alacarte-title"
      >
        <div className="alacarte-head">
          <h2 id="alacarte-title">Reports A La Carte</h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="alacarte-body">
          <aside className="alacarte-catalog" aria-label="Available reports">
            <div className="alacarte-catalog-label">Available reports</div>
            <div className="alacarte-tiles">
              {REPORT_CATALOG.map((entry) => (
                <ReportTile
                  key={entry.key}
                  entry={entry}
                  selected={selectedReport === entry.key}
                  onSelect={() => entry.available && setSelectedReport(entry.key)}
                />
              ))}
            </div>
          </aside>

          <section className="alacarte-options">
            {isCardReport ? (
              <>
                <header className="alacarte-options-head">
                  <h3>{isTravelTreat ? "Travel & Treats" : "Pullman Menu Cards"}</h3>
                  <p>
                    {isTravelTreat
                      ? "Cards for Fish & Chips and Travel & Treats bookings. Pick a service to print every card on it, or search by name / booking reference for a single reprint."
                      : "Pre-printed cards for each booking on a Pullman service. Pick a service to print every card on it, or search by name / booking reference for a single reprint."}
                  </p>
                </header>

                <ModeToggle mode={mode} onChange={setMode} />

                {mode === "service" ? (
                  <ServicePane
                    serviceTab={serviceTab}
                    setServiceTab={setServiceTab}
                    upcomingServices={upcomingServices.data?.services ?? []}
                    upcomingLoading={upcomingServices.loading}
                    upcomingError={upcomingServices.error}
                    rangeServices={rangeServices.data?.services ?? []}
                    rangeLoading={rangeServices.loading}
                    rangeError={rangeServices.error}
                    rangeFrom={rangeFrom}
                    rangeTo={rangeTo}
                    setRangeFrom={setRangeFrom}
                    setRangeTo={setRangeTo}
                    selectedService={selectedService}
                    onSelectService={setSelectedService}
                  />
                ) : (
                  <BookingPane
                    q={searchQ}
                    onQChange={setSearchQ}
                    bookings={search.data?.bookings ?? []}
                    loading={search.loading}
                    error={search.error}
                    truncated={Boolean(search.data?.truncated)}
                    selectedBooking={selectedBooking}
                    onSelectBooking={setSelectedBooking}
                  />
                )}

                <RefreshRow
                  lastRefreshAt={activeServices.data?.last_refresh?.at ?? null}
                  lastRefreshRunner={activeServices.data?.last_refresh?.runner ?? null}
                  refreshBusy={refresh.busy}
                  refreshStatus={refresh.request?.status ?? null}
                  refreshMessage={refresh.error ?? refresh.request?.message ?? null}
                  onTriggerRefresh={refresh.trigger}
                />

                <CardSizeSection
                  size={printOptions.size}
                  onSelect={(s) =>
                    setPrintOptions((prev) => ({ ...prev, size: s }))
                  }
                  scaleFoodToFit={printOptions.scaleFoodToFit}
                  onScaleFoodToFit={(v) =>
                    setPrintOptions((prev) => ({ ...prev, scaleFoodToFit: v }))
                  }
                  showScaleToFit={!isTravelTreat}
                />

                {/* Travel & Treats uses one fixed simple card, so no style picker. */}
                {!isTravelTreat && (
                  <TemplateSelectSection
                    selected={printOptions.template}
                    onSelect={(t) =>
                      setPrintOptions((prev) => ({ ...prev, template: t }))
                    }
                  />
                )}

                <PrintModeSection
                  printMode={printOptions.printMode}
                  highlightDietary={printOptions.highlightDietary}
                  onPrintMode={(m) =>
                    setPrintOptions((prev) => ({ ...prev, printMode: m }))
                  }
                  onHighlightDietary={(v) =>
                    setPrintOptions((prev) => ({ ...prev, highlightDietary: v }))
                  }
                  onEditDietary={() => setDietaryOpen(true)}
                  editDietaryEnabled={mode === "service" && selectedService != null && (cards.data?.bookings.length ?? 0) > 0}
                  editDietaryTitle={
                    mode !== "service"
                      ? "Pick a whole service to edit its dietary comments"
                      : !selectedService
                        ? "Select a service first"
                        : (cards.data?.bookings.length ?? 0) === 0
                          ? "No bookings on this service yet"
                          : "List every booking on this service and edit the dietary comment its card prints"
                  }
                />
                <DietaryCommentsDialog
                  open={dietaryOpen}
                  service={mode === "service" ? selectedService : null}
                  bookings={cards.data?.bookings ?? []}
                  onClose={() => setDietaryOpen(false)}
                  onSaved={() => { void cards.refresh(); }}
                />

                <footer className="alacarte-footer">
                  <div className="alacarte-footer-summary">
                    {mode === "service" ? (
                      selectedService ? (
                        <SelectedServiceSummary
                          service={selectedService}
                          bookingsKnown={cards.data?.bookings.length ?? null}
                          loading={cards.loading}
                        />
                      ) : (
                        <span className="alacarte-footer-hint">
                          Select a service above to enable Generate PDF.
                        </span>
                      )
                    ) : selectedBooking ? (
                      <SelectedBookingSummary booking={selectedBooking} />
                    ) : (
                      <span className="alacarte-footer-hint">
                        Search and select one booking to enable Generate PDF.
                      </span>
                    )}
                    {genError && (
                      <span className="alacarte-footer-error" role="alert">
                        {genError}
                      </span>
                    )}
                  </div>
                  <div className="alacarte-footer-actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void onPreview()}
                      disabled={previewDisabled}
                      title="Render the first card and open it in a new tab"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => void onGenerate()}
                      disabled={generateDisabled}
                    >
                      {generateLabel}
                    </button>
                  </div>
                </footer>
              </>
            ) : isCarriageLabels ? (
              <CarriageLabelsPane open={open} />
            ) : (
              <ComingSoonPanel reportKey={selectedReport} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

interface ReportTileProps {
  entry: ReportCatalogEntry;
  selected: boolean;
  onSelect: () => void;
}

function ReportTile({ entry, selected, onSelect }: ReportTileProps) {
  const className = [
    "alacarte-tile",
    selected ? "alacarte-tile--selected" : "",
    entry.available ? "" : "alacarte-tile--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      onClick={onSelect}
      disabled={!entry.available}
      aria-pressed={selected}
    >
      <div className="alacarte-tile-thumb">
        <ReportThumbnail variant={entry.key} />
        {entry.badge && (
          <span className="alacarte-tile-badge">{entry.badge}</span>
        )}
      </div>
      <div className="alacarte-tile-body">
        <div className="alacarte-tile-name">{entry.name}</div>
        <div className="alacarte-tile-blurb">{entry.blurb}</div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mode toggle — Whole service vs Just one card
// ---------------------------------------------------------------------------
interface ModeToggleProps {
  mode: SelectionMode;
  onChange: (m: SelectionMode) => void;
}

function ModeToggle({ mode, onChange }: ModeToggleProps) {
  return (
    <div className="alacarte-modetoggle" role="radiogroup" aria-label="What to print">
      <button
        type="button"
        role="radio"
        aria-checked={mode === "service"}
        className={
          "alacarte-modetoggle-btn" +
          (mode === "service" ? " alacarte-modetoggle-btn--active" : "")
        }
        onClick={() => onChange("service")}
      >
        <span className="alacarte-modetoggle-icon" aria-hidden="true">📋</span>
        <span className="alacarte-modetoggle-label">A whole service</span>
        <span className="alacarte-modetoggle-blurb">
          Every card for one Pullman service
        </span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={mode === "booking"}
        className={
          "alacarte-modetoggle-btn" +
          (mode === "booking" ? " alacarte-modetoggle-btn--active" : "")
        }
        onClick={() => onChange("booking")}
      >
        <span className="alacarte-modetoggle-icon" aria-hidden="true">🔍</span>
        <span className="alacarte-modetoggle-label">Just one card</span>
        <span className="alacarte-modetoggle-blurb">
          Find a guest by name or booking reference
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service pane — tabbed: Upcoming | Find by date
// ---------------------------------------------------------------------------
interface ServicePaneProps {
  serviceTab: ServiceTab;
  setServiceTab: (t: ServiceTab) => void;
  upcomingServices: MenuCardService[];
  upcomingLoading: boolean;
  upcomingError: string | null;
  rangeServices: MenuCardService[];
  rangeLoading: boolean;
  rangeError: string | null;
  rangeFrom: string;
  rangeTo: string;
  setRangeFrom: (s: string) => void;
  setRangeTo: (s: string) => void;
  selectedService: MenuCardService | null;
  onSelectService: (s: MenuCardService) => void;
}

function ServicePane(props: ServicePaneProps) {
  const {
    serviceTab,
    setServiceTab,
    upcomingServices,
    upcomingLoading,
    upcomingError,
    rangeServices,
    rangeLoading,
    rangeError,
    rangeFrom,
    rangeTo,
    setRangeFrom,
    setRangeTo,
    selectedService,
    onSelectService,
  } = props;

  const upcomingShown = upcomingServices.slice(0, UPCOMING_LIMIT);

  return (
    <section className="alacarte-section">
      <div className="alacarte-tabbar" role="tablist" aria-label="Service finder">
        <button
          type="button"
          role="tab"
          aria-selected={serviceTab === "upcoming"}
          className={
            "alacarte-tab" +
            (serviceTab === "upcoming" ? " alacarte-tab--active" : "")
          }
          onClick={() => setServiceTab("upcoming")}
        >
          Next {UPCOMING_LIMIT} services
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={serviceTab === "date-range"}
          className={
            "alacarte-tab" +
            (serviceTab === "date-range" ? " alacarte-tab--active" : "")
          }
          onClick={() => setServiceTab("date-range")}
        >
          Find by date
        </button>
      </div>

      {serviceTab === "upcoming" ? (
        <UpcomingServiceList
          services={upcomingShown}
          loading={upcomingLoading}
          error={upcomingError}
          selectedService={selectedService}
          onSelectService={onSelectService}
          totalAvailable={upcomingServices.length}
        />
      ) : (
        <DateRangeServiceList
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          setRangeFrom={setRangeFrom}
          setRangeTo={setRangeTo}
          services={rangeServices}
          loading={rangeLoading}
          error={rangeError}
          selectedService={selectedService}
          onSelectService={onSelectService}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Upcoming list — visually distinct (the marquee path)
// ---------------------------------------------------------------------------
interface UpcomingServiceListProps {
  services: MenuCardService[];
  loading: boolean;
  error: string | null;
  selectedService: MenuCardService | null;
  onSelectService: (s: MenuCardService) => void;
  totalAvailable: number;
}

function UpcomingServiceList(props: UpcomingServiceListProps) {
  const { services, loading, error, selectedService, onSelectService, totalAvailable } = props;
  return (
    <div className="alacarte-upcoming">
      {error ? (
        <div className="alacarte-empty alacarte-empty--error">
          Couldn’t load upcoming services: {error}
        </div>
      ) : loading && services.length === 0 ? (
        <div className="alacarte-empty">Loading upcoming services…</div>
      ) : services.length === 0 ? (
        <div className="alacarte-empty">
          No services scheduled in the next {UPCOMING_RANGE_DAYS} days. Try the
          Find by date tab to look further out.
        </div>
      ) : (
        <>
          {services.map((svc) => {
            const isSelected = isServiceSelected(svc, selectedService);
            const dl = daySuperLabel(svc.service_date);
            return (
              <button
                key={serviceKey(svc)}
                type="button"
                className={
                  "alacarte-upcoming-card" +
                  (isSelected ? " alacarte-upcoming-card--selected" : "")
                }
                onClick={() => onSelectService(svc)}
                aria-pressed={isSelected}
              >
                <div className="alacarte-upcoming-when">
                  {dl.tag && <span className="alacarte-upcoming-tag">{dl.tag}</span>}
                  <span className="alacarte-upcoming-day">{dl.date}</span>
                  <span className="alacarte-upcoming-time">
                    {extractDeparture(svc.service_time_label)}
                  </span>
                </div>
                <div className="alacarte-upcoming-desc">{svc.journey_description}</div>
                <div className="alacarte-upcoming-meta">
                  <span><strong>{svc.booking_count}</strong> bookings</span>
                  <span className="alacarte-service-dot">·</span>
                  <span><strong>{svc.total_diners}</strong> diners</span>
                  <span className="alacarte-service-dot">·</span>
                  <span>{svc.carriages_used ?? "—"}</span>
                  {svc.has_dietary_count > 0 && (
                    <>
                      <span className="alacarte-service-dot">·</span>
                      <span className="alacarte-service-diet">
                        {svc.has_dietary_count} dietary
                      </span>
                    </>
                  )}
                </div>
              </button>
            );
          })}
          {totalAvailable > services.length && (
            <div className="alacarte-upcoming-more">
              {totalAvailable - services.length} more service
              {totalAvailable - services.length === 1 ? "" : "s"} in the next {UPCOMING_RANGE_DAYS} days
              — switch to “Find by date” to see them all.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date-range list — same visual pattern as before, behind a tab now
// ---------------------------------------------------------------------------
interface DateRangeServiceListProps {
  rangeFrom: string;
  rangeTo: string;
  setRangeFrom: (s: string) => void;
  setRangeTo: (s: string) => void;
  services: MenuCardService[];
  loading: boolean;
  error: string | null;
  selectedService: MenuCardService | null;
  onSelectService: (s: MenuCardService) => void;
}

function DateRangeServiceList(props: DateRangeServiceListProps) {
  const {
    rangeFrom,
    rangeTo,
    setRangeFrom,
    setRangeTo,
    services,
    loading,
    error,
    selectedService,
    onSelectService,
  } = props;
  return (
    <>
      <div className="alacarte-row">
        <label className="alacarte-field">
          <span>From</span>
          <input
            type="date"
            value={rangeFrom}
            onChange={(e) => setRangeFrom(e.target.value)}
            max={rangeTo}
          />
        </label>
        <label className="alacarte-field">
          <span>To</span>
          <input
            type="date"
            value={rangeTo}
            onChange={(e) => setRangeTo(e.target.value)}
            min={rangeFrom}
          />
        </label>
        <span className="alacarte-row-meta">
          {services.length} service{services.length === 1 ? "" : "s"} in range
        </span>
      </div>

      <div className="alacarte-services" role="listbox" aria-label="Services">
        {error ? (
          <div className="alacarte-empty alacarte-empty--error">
            Couldn’t load services: {error}
          </div>
        ) : loading && services.length === 0 ? (
          <div className="alacarte-empty">Loading services…</div>
        ) : services.length === 0 ? (
          <div className="alacarte-empty">
            No services in this date range. Try widening the dates above.
          </div>
        ) : (
          services.map((svc) => {
            const isSelected = isServiceSelected(svc, selectedService);
            return (
              <button
                key={serviceKey(svc)}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={
                  "alacarte-service" +
                  (isSelected ? " alacarte-service--selected" : "")
                }
                onClick={() => onSelectService(svc)}
              >
                <div className="alacarte-service-line1">
                  <span className="alacarte-service-day">{svc.service_day_label}</span>
                  <span className="alacarte-service-dot">·</span>
                  <span className="alacarte-service-time">
                    {extractDeparture(svc.service_time_label)}
                  </span>
                  <span className="alacarte-service-dot">·</span>
                  <span className="alacarte-service-desc">{svc.journey_description}</span>
                </div>
                <div className="alacarte-service-line2">
                  <span>{svc.booking_count} bookings</span>
                  <span className="alacarte-service-dot">·</span>
                  <span>{svc.total_diners} diners</span>
                  <span className="alacarte-service-dot">·</span>
                  <span>{svc.carriages_used ?? "—"}</span>
                  {svc.has_dietary_count > 0 && (
                    <>
                      <span className="alacarte-service-dot">·</span>
                      <span className="alacarte-service-diet">
                        {svc.has_dietary_count} dietary note
                        {svc.has_dietary_count === 1 ? "" : "s"}
                      </span>
                    </>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Booking pane — search + result list, single-card mode
// ---------------------------------------------------------------------------
interface BookingPaneProps {
  q: string;
  onQChange: (s: string) => void;
  bookings: MenuCardBooking[];
  loading: boolean;
  error: string | null;
  truncated: boolean;
  selectedBooking: MenuCardBooking | null;
  onSelectBooking: (b: MenuCardBooking) => void;
}

function BookingPane(props: BookingPaneProps) {
  const {
    q,
    onQChange,
    bookings,
    loading,
    error,
    truncated,
    selectedBooking,
    onSelectBooking,
  } = props;
  return (
    <section className="alacarte-section">
      <label className="alacarte-search">
        <span className="alacarte-search-icon" aria-hidden="true">🔍</span>
        <input
          type="search"
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search by guest name or booking reference…"
          autoFocus
        />
        {q && (
          <button
            type="button"
            className="alacarte-search-clear"
            onClick={() => onQChange("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </label>

      <div className="alacarte-search-results" role="listbox" aria-label="Bookings">
        {error ? (
          <div className="alacarte-empty alacarte-empty--error">
            Couldn’t search bookings: {error}
          </div>
        ) : loading && bookings.length === 0 ? (
          <div className="alacarte-empty">Searching…</div>
        ) : bookings.length === 0 ? (
          <div className="alacarte-empty">
            {q
              ? "No bookings match that search in the next 60 days."
              : "Type a guest name or booking reference to find a card."}
          </div>
        ) : (
          <>
            {bookings.map((b) => {
              const isSelected = selectedBooking?.id === b.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={
                    "alacarte-booking" +
                    (isSelected ? " alacarte-booking--selected" : "")
                  }
                  onClick={() => onSelectBooking(b)}
                >
                  <div className="alacarte-booking-line1">
                    <span className="alacarte-booking-name">{b.account_name}</span>
                    <span className="alacarte-booking-ref">#{b.booking_reference}</span>
                    {(b.has_dietary_notes || Boolean(b.dietary_override)) && (
                      <span className="alacarte-booking-diet" title={b.dietary_override ?? b.booking_notes ?? undefined}>
                        Dietary
                      </span>
                    )}
                  </div>
                  <div className="alacarte-booking-line2">
                    <span>{b.service_day_label}</span>
                    <span className="alacarte-service-dot">·</span>
                    <span>{extractDeparture(b.service_time_label)}</span>
                    <span className="alacarte-service-dot">·</span>
                    <span>{b.journey_description}</span>
                  </div>
                  <div className="alacarte-booking-line3">
                    <span>{b.carriage}</span>
                    <span className="alacarte-service-dot">·</span>
                    <span>Table {b.table_name}</span>
                    <span className="alacarte-service-dot">·</span>
                    <span>Seats {b.seats}</span>
                    {b.footfall != null && (
                      <>
                        <span className="alacarte-service-dot">·</span>
                        <span>{b.footfall} diner{b.footfall === 1 ? "" : "s"}</span>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
            {truncated && (
              <div className="alacarte-search-truncated">
                Showing first {bookings.length} matches — refine the search to
                narrow down further.
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Refresh row — last-refresh metadata + Refresh now button
// ---------------------------------------------------------------------------
interface RefreshRowProps {
  lastRefreshAt: string | null;
  lastRefreshRunner: string | null;
  refreshBusy: boolean;
  refreshStatus: string | null;
  refreshMessage: string | null;
  onTriggerRefresh: () => Promise<void>;
}

function RefreshRow(props: RefreshRowProps) {
  const {
    lastRefreshAt,
    lastRefreshRunner,
    refreshBusy,
    refreshStatus,
    refreshMessage,
    onTriggerRefresh,
  } = props;
  let label = "↻ Refresh now";
  if (refreshBusy) {
    if (refreshStatus === "pending") label = "Queued…";
    else if (refreshStatus === "running") label = "Running…";
    else if (refreshStatus === "completed") label = "Done";
    else label = "Working…";
  }
  return (
    <div className="alacarte-refresh-row">
      <span className="alacarte-refresh-meta">
        Last refresh:{" "}
        <strong>{formatPrettyDateTime(lastRefreshAt)}</strong>
        {lastRefreshRunner ? ` · ${lastRefreshRunner}` : ""}
      </span>
      <span className="alacarte-refresh-control">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => void onTriggerRefresh()}
          disabled={refreshBusy}
          title={refreshMessage ?? undefined}
        >
          {label}
        </button>
        {refreshMessage && !refreshBusy && (
          <span className="alacarte-refresh-msg" title={refreshMessage}>
            {refreshMessage}
          </span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template select — radio group
// ---------------------------------------------------------------------------
interface TemplateSelectSectionProps {
  selected: TemplateStyle;
  onSelect: (t: TemplateStyle) => void;
}

function TemplateSelectSection({ selected, onSelect }: TemplateSelectSectionProps) {
  return (
    <section className="alacarte-section">
      <h4 className="alacarte-section-title">Template style</h4>
      <div className="alacarte-templates">
        {TEMPLATE_REGISTRY.map((tpl) => {
          const isSelected = tpl.key === selected;
          return (
            <button
              key={tpl.key}
              type="button"
              className={
                "alacarte-template" +
                (isSelected ? " alacarte-template--selected" : "")
              }
              onClick={() => onSelect(tpl.key)}
              aria-pressed={isSelected}
            >
              <span className="alacarte-template-radio" aria-hidden="true">
                {isSelected ? "●" : "○"}
              </span>
              <span className="alacarte-template-text">
                <span className="alacarte-template-name">{tpl.name}</span>
                <span className="alacarte-template-blurb">{tpl.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card size — A6 portrait vs A5 landscape
// ---------------------------------------------------------------------------
interface CardSizeSectionProps {
  size: CardSize;
  onSelect: (s: CardSize) => void;
  scaleFoodToFit: boolean;
  onScaleFoodToFit: (v: boolean) => void;
  showScaleToFit: boolean;
}

function CardSizeSection({
  size,
  onSelect,
  scaleFoodToFit,
  onScaleFoodToFit,
  showScaleToFit,
}: CardSizeSectionProps) {
  const selected = CARD_SIZE_REGISTRY.find((s) => s.key === size);
  return (
    <section className="alacarte-section">
      <h4 className="alacarte-section-title">Card size</h4>
      <div className="alacarte-printmode">
        {CARD_SIZE_REGISTRY.map((s) => (
          <button
            key={s.key}
            type="button"
            className={
              "alacarte-mode" + (size === s.key ? " alacarte-mode--selected" : "")
            }
            onClick={() => onSelect(s.key)}
            aria-pressed={size === s.key}
            title={s.blurb}
          >
            <span aria-hidden="true">{size === s.key ? "●" : "○"}</span>
            <span>{s.name}</span>
          </button>
        ))}
      </div>
      {selected && <p className="alacarte-size-hint">{selected.blurb}</p>}
      {showScaleToFit && (
        <label className="alacarte-checkrow">
          <input
            type="checkbox"
            checked={scaleFoodToFit}
            onChange={(e) => onScaleFoodToFit(e.target.checked)}
          />
          <span>
            Scale food lines to fit — size the menu order lines to fill the
            card, so a short menu prints larger and doesn&rsquo;t bunch at the top.
          </span>
        </label>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Print mode — colour vs B&W + dietary toggle
// ---------------------------------------------------------------------------
interface PrintModeSectionProps {
  printMode: PrintMode;
  highlightDietary: boolean;
  onPrintMode: (m: PrintMode) => void;
  onHighlightDietary: (v: boolean) => void;
  onEditDietary: () => void;
  editDietaryEnabled: boolean;
  editDietaryTitle: string;
}

function PrintModeSection(props: PrintModeSectionProps) {
  const { printMode, highlightDietary, onPrintMode, onHighlightDietary, onEditDietary, editDietaryEnabled, editDietaryTitle } = props;
  return (
    <section className="alacarte-section">
      <h4 className="alacarte-section-title">Print mode</h4>
      <div className="alacarte-printmode">
        <button
          type="button"
          className={
            "alacarte-mode" +
            (printMode === "colour" ? " alacarte-mode--selected" : "")
          }
          onClick={() => onPrintMode("colour")}
          aria-pressed={printMode === "colour"}
        >
          <span aria-hidden="true">{printMode === "colour" ? "●" : "○"}</span>
          <span>Full colour</span>
        </button>
        <button
          type="button"
          className={
            "alacarte-mode" +
            (printMode === "bw" ? " alacarte-mode--selected" : "")
          }
          onClick={() => onPrintMode("bw")}
          aria-pressed={printMode === "bw"}
        >
          <span aria-hidden="true">{printMode === "bw" ? "●" : "○"}</span>
          <span>Black &amp; white</span>
        </button>
      </div>
      <div className="alacarte-checkrow-wrap">
        <label className="alacarte-checkrow">
          <input
            type="checkbox"
            checked={highlightDietary}
            onChange={(e) => onHighlightDietary(e.target.checked)}
          />
          <span>
            Highlight dietary notes from booking (recommended — flagged cards print
            with a banner naming the requirement).
          </span>
        </label>
        <button
          type="button"
          className="btn btn--ghost alacarte-diet-edit"
          onClick={onEditDietary}
          disabled={!editDietaryEnabled}
          title={editDietaryTitle}
        >
          Edit Dietary Comments
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Selection summaries (footer, above Generate button)
// ---------------------------------------------------------------------------
interface SelectedServiceSummaryProps {
  service: MenuCardService;
  bookingsKnown: number | null;
  loading: boolean;
}

function SelectedServiceSummary({
  service,
  bookingsKnown,
  loading,
}: SelectedServiceSummaryProps) {
  return (
    <span className="alacarte-selected-summary">
      <strong>Selected:</strong>{" "}
      {service.service_day_label} ·{" "}
      {extractDeparture(service.service_time_label)} ·{" "}
      {service.journey_description}
      {" — "}
      {loading
        ? "loading bookings…"
        : bookingsKnown === null
          ? `${service.booking_count} bookings expected`
          : `${bookingsKnown} card${bookingsKnown === 1 ? "" : "s"} ready to print`}
    </span>
  );
}

function SelectedBookingSummary({ booking }: { booking: MenuCardBooking }) {
  return (
    <span className="alacarte-selected-summary">
      <strong>Selected:</strong>{" "}
      {booking.account_name} (#{booking.booking_reference}) — {booking.service_day_label} · {extractDeparture(booking.service_time_label)} · {booking.carriage} · Table {booking.table_name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Coming-soon placeholder for the Travel & Treats tile
// ---------------------------------------------------------------------------
function ComingSoonPanel({ reportKey }: { reportKey: ReportKey }) {
  const copy = useMemo(() => {
    if (reportKey === "travel-and-treats") {
      return {
        title: "Travel & Treats",
        body:
          "Cards for Fish & Chips, Cream Tea, and other Travel & Treats services. The data shape is broadly the same as Pullman, just with a different journey-type filter at refresh time. We’ll add this once the Pullman menu cards are bedded in.",
      };
    }
    return { title: "Coming soon", body: "More reports will appear here as they’re built." };
  }, [reportKey]);
  return (
    <div className="alacarte-comingsoon">
      <h3>{copy.title}</h3>
      <p>{copy.body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------
function serviceKey(s: MenuCardService): string {
  return `${s.service_date}|${s.service_time_label}|${s.journey_description}`;
}

function isServiceSelected(s: MenuCardService, selected: MenuCardService | null): boolean {
  if (!selected) return false;
  return (
    selected.service_date === s.service_date &&
    selected.service_time_label === s.service_time_label &&
    selected.journey_description === s.journey_description
  );
}
